import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useHomeRooms } from '../../pages/client/home/useHomeRooms';
import { useSelectedRoom } from '../../hooks/router/useSelectedRoom';
import { useArchivedRoomsList } from '../../state/room/roomStateInfo';
import { useRoomTicketsInfo, Ticket } from '../../state/room/roomTicketsInfo';
import { useRoomsProcessInfo, RoomProcessInfo } from '../../state/room/roomProcessInfo';
import { useRoomRoleAccessing } from '../../state/room/roomRoleAccessing';
import { useAtom, useAtomValue } from 'jotai';
import { roomListForceRefreshAtom } from '../../state/room/roomListForceRefresh';
import { getHomeRoomPath } from '../../pages/pathUtils';
import { getCanonicalAliasOrRoomId } from '../../utils/matrix';
import { openCreateRoom } from '../../../client/action/navigation';
import { factoryRoomIdByCreateTime } from '../../utils/sort';

import { CaseListPanel } from './CaseListPanel';
import { CaseListItem, CaseListItemData } from './CaseListItem';
import { StatusTabItem } from './StatusTabs';
import { ArchiveFilterValue } from './ArchiveFilter';

const PRIORITY_COLORS: Record<number, string> = {
  0: 'var(--curve-priority-0, #E5E5E5)',
  1: 'var(--curve-priority-1, #E5E5E5)',
  2: 'var(--curve-priority-2, #D1D5DB)',
  3: 'var(--curve-priority-3, #FCA5A5)',
  4: 'var(--curve-priority-4, #93C5FD)',
  5: 'var(--curve-priority-5, #FD7E14)',
  6: 'var(--curve-priority-6, #EF4444)',
};

type TFunction = (key: string, opts?: Record<string, unknown>) => string;

const getPriorityLabels = (t: TFunction): Record<number, string> => ({
  0: t('Priority.not_set'),
  1: t('Priority.low'),
  2: t('Priority.medium'),
  3: t('Priority.high'),
  4: t('Priority.vip'),
  5: t('Priority.urgent'),
  6: t('Priority.very_urgent'),
});

const getStatusFromProcess = (
  info: RoomProcessInfo | null | undefined,
  t: TFunction
) => {
  if (!info || typeof info.process_action !== 'number') {
    return { status: 'pending' as const, statusLabel: t('CaseStatus.pending'), statusColor: 'orange' };
  }
  if (info.process_action === 0) {
    if (info.process_state === 0)
      return { status: 'pending' as const, statusLabel: t('CaseStatus.pending'), statusColor: 'orange' };
    return { status: 'in_progress' as const, statusLabel: t('CaseStatus.in_progress'), statusColor: 'blue' };
  }
  if (info.process_action === 3)
    return { status: 'closed' as const, statusLabel: t('CaseStatus.closed'), statusColor: 'gray' };
  return { status: 'in_progress' as const, statusLabel: t('CaseStatus.in_progress'), statusColor: 'blue' };
};

const formatTime = (isoString: string | undefined, t: TFunction): string => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 1) return t('Time.yesterday');
  if (diffDays < 7) return t('Time.days_ago', { count: diffDays });
  return d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
};

type CaseListContainerProps = {
  setHasRoomSelected: React.Dispatch<React.SetStateAction<boolean>>;
  setRoomClosed: React.Dispatch<React.SetStateAction<boolean>>;
};

export function CaseListContainer({ setHasRoomSelected, setRoomClosed }: CaseListContainerProps) {
  const { t } = useTranslation();
  const mx = useMatrixClient();
  const navigate = useNavigate();

  const PRIORITY_LABELS = getPriorityLabels(t);
  const PRIORITY_ITEMS = Object.entries(PRIORITY_LABELS)
    .filter(([k]) => Number(k) > 0)
    .map(([k, v]) => ({ id: k, label: v }));
  const rooms = useHomeRooms();
  const selectedRoomId = useSelectedRoom();
  const { canShowPriority } = useRoomRoleAccessing(mx);

  const { archivedRoomsList, fetchArchivedRooms } = useArchivedRoomsList(mx);
  const { tickets, fetchTicketsForRooms } = useRoomTicketsInfo(mx);

  const archivedRoomIds = useMemo(() => {
    if (!archivedRoomsList?.rooms) return [] as string[];
    return archivedRoomsList.rooms
      .map((r: unknown) =>
        typeof r === 'string' ? r : (r as Record<string, string>)?.room_id ?? ''
      )
      .filter(Boolean) as string[];
  }, [archivedRoomsList]);

  const [activeStatus, setActiveStatus] = useState<StatusTabItem['id']>('all');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [priorityValue, setPriorityValue] = useState('');
  const [archiveValue, setArchiveValue] = useState<ArchiveFilterValue>('active');

  const forceRefreshTrigger = useAtomValue(roomListForceRefreshAtom);
  useEffect(() => {
    if (forceRefreshTrigger > 0) {
      const originalStatus = activeStatus;
      setActiveStatus('pending');
      setTimeout(() => {
        setActiveStatus(originalStatus);
      }, 50); // 極短延遲切換回原始狀態，模擬手動刷新
    }
  }, [forceRefreshTrigger]);

  const [userProjects, setUserProjects] = useState<string[]>([]);

  useEffect(() => {
    let isMounted = true;
    const fetchUserProjects = async () => {
      try {
        const profile = await mx.getProfileInfo(mx.getUserId());
        if (!isMounted) return;
        const projectStr = (profile as any).project || '';
        const projectList = projectStr.split(',').map((p: string) => p.trim()).filter((p: string) => p !== '');
        setUserProjects(projectList);
      } catch (e) {
        console.error('Failed to fetch user projects for filter', e);
      }
    };
    fetchUserProjects();
    return () => { isMounted = false; };
  }, [mx]);

  const activeRooms = useMemo(
    () => rooms.filter((id) => !archivedRoomIds.includes(id)),
    [rooms, archivedRoomIds]
  );

  const displayRooms = useMemo(() => {
    if (archiveValue === 'active') return activeRooms;
    if (archiveValue === 'archived') return archivedRoomIds;
    return [...activeRooms, ...archivedRoomIds];
  }, [archiveValue, activeRooms, archivedRoomIds]);

  // 全域監聽 Room 名稱更新以強制觸發重算
  const [, setTick] = useState(0);
  const forceUpdate = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const handleRoomNameUpdate = (room: any) => {
      // 僅在更新的房間屬於此列表時觸發
      if (displayRooms.includes(room.roomId)) {
        forceUpdate();
      }
    };
    mx.on('Room.name', handleRoomNameUpdate);
    return () => {
      mx.removeListener('Room.name', handleRoomNameUpdate);
    };
  }, [mx, displayRooms, forceUpdate]);

  const { roomsInfo, fetchInfoForRooms } = useRoomsProcessInfo(mx, displayRooms);

  useEffect(() => { fetchArchivedRooms(); }, [fetchArchivedRooms]);
  useEffect(() => {
    if (displayRooms.length > 0) {
      fetchTicketsForRooms(displayRooms);
      fetchInfoForRooms();
    }
  }, [displayRooms, fetchTicketsForRooms, fetchInfoForRooms]);

  const ticketMap = useMemo(() => {
    const m = new Map<string, Ticket>();
    if (tickets) tickets.forEach((t) => m.set(t.uuid, t));
    return m;
  }, [tickets]);

  const projectItems = useMemo(() => {
    return userProjects.map((p) => ({ id: p, label: p }));
  }, [userProjects]);

  const caseItems = useMemo<CaseListItemData[]>(() => {
    const sorted = [...displayRooms].sort(factoryRoomIdByCreateTime(mx));
    return sorted.reduce<CaseListItemData[]>((acc, roomId) => {
      const room = mx.getRoom(roomId);
      // 加固：確保房間物件存在且名稱屬性為字串，防止 SDK 內部屬性存取引發異常
      if (!room || typeof room.name !== 'string') return acc;

      const ticket = ticketMap.get(roomId);
      const processInfo = roomsInfo[roomId];
      const { status, statusLabel, statusColor } = getStatusFromProcess(processInfo, t);
      const priority = canShowPriority && ticket
        ? PRIORITY_LABELS[ticket.priority ?? 0]
        : undefined;
      const priorityColor = canShowPriority && ticket
        ? PRIORITY_COLORS[ticket.priority ?? 0]
        : undefined;

      const item: CaseListItemData = {
        id: roomId,
        status,
        statusLabel,
        statusColor,
        priority,
        priorityColor,
        caseNo: ticket?.ticket_no,
        docNo: ticket?.doc_no,
        subject: room.name,
        excerpt: ticket?.summary,
        time: formatTime(ticket?.create_time, t),
        archived: archivedRoomIds.includes(roomId),
      };

      if (activeStatus !== 'all' && item.status !== activeStatus) return acc;

      const projectEvent = room.currentState.getStateEvents('m.room.project', '');
      const roomProject = projectEvent?.getContent()?.project;
      if (selectedProjectId && roomProject !== selectedProjectId) return acc;

      if (priorityValue && String(ticket?.priority ?? 0) !== priorityValue) return acc;
      if (searchValue) {
        const q = searchValue.toLowerCase();
        const match =
          item.caseNo?.toLowerCase().includes(q) ||
          item.docNo?.toLowerCase().includes(q) ||
          item.subject.toLowerCase().includes(q);
        if (!match) return acc;
      }

      acc.push(item);
      return acc;
    }, []);
  }, [displayRooms, mx, ticketMap, roomsInfo, canShowPriority, activeStatus,
      selectedProjectId, priorityValue, searchValue, archivedRoomIds, t]);

  const statusCounts = useMemo(() => {
    const counts = { all: 0, pending: 0, in_progress: 0, closed: 0 };
    displayRooms.forEach((roomId) => {
      const { status } = getStatusFromProcess(roomsInfo[roomId], t);
      counts.all += 1;
      counts[status] = (counts[status] ?? 0) + 1;
    });
    return counts;
  }, [displayRooms, roomsInfo, t]);

  const statusItems: StatusTabItem[] = [
    { id: 'all', label: t('StatusTab.all'), count: statusCounts.all },
    { id: 'pending', label: t('StatusTab.pending'), count: statusCounts.pending },
    { id: 'in_progress', label: t('StatusTab.in_progress'), count: statusCounts.in_progress },
    { id: 'closed', label: t('StatusTab.closed'), count: statusCounts.closed },
  ];

  const handleRoomSelect = useCallback(
    (roomId: string) => {
      const room = mx.getRoom(roomId);
      if (!room) return;
      const path = getHomeRoomPath(getCanonicalAliasOrRoomId(mx, roomId));
      setRoomClosed(false);
      setHasRoomSelected(true);
      navigate(path);
    },
    [mx, navigate, setRoomClosed, setHasRoomSelected]
  );

  return (
    <CaseListPanel
      onAddCase={() => openCreateRoom()}
      statusItems={statusItems}
      activeStatusId={activeStatus}
      onStatusSelect={setActiveStatus}
      projectItems={projectItems}
      selectedProjectId={selectedProjectId}
      onProjectSelect={setSelectedProjectId}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      priorityItems={PRIORITY_ITEMS}
      priorityValue={priorityValue}
      onPriorityChange={setPriorityValue}
      archiveValue={archiveValue}
      onArchiveChange={setArchiveValue}
    >
      {caseItems.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '160px',
            color: 'var(--curve-text-placeholder, #94A3B8)',
            fontSize: '13px',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '24px' }}>📭</span>
          {searchValue ? t('CaseList.no_results') : t('CaseList.no_cases')}
        </div>
      ) : (
        caseItems.map((item) => (
          <CaseListItem
            key={item.id}
            item={item}
            isSelected={item.id === selectedRoomId}
            onSelect={handleRoomSelect}
          />
        ))
      )}
    </CaseListPanel>
  );
}
